import { createContext, useContext, useState, useCallback, useMemo } from "react";

const dict = {
  sr: {
    nav: { home: "Početna", about: "O nama", wines: "Naša vina", shop: "Prodavnica", contact: "Kontakt", cart: "Korpa", visit: "Posetite nas" },
    hero: {
      eyebrow: "PORODIČNA VINARIJA",
      title: "Stanimirović",
      subtitle: "Tradicija u svakoj kapi. Vino rođeno iz tišine podruma i topline porodičnih ruku.",
      cta: "Otkrijte Noćnu Tajnu",
      ctaSecondary: "Naša priča",
    },
    identity: {
      eyebrow: "NAŠ IDENTITET",
      title: "Jedan znak. Jedna priča.",
      body: "Slovo \u201eS\u201c upisano u vinsku čašu — sažetak svega što radimo. Posveta, preciznost i ljubav prema vinu koje pripoveda našu porodičnu istoriju.",
    },
    heritage: {
      eyebrow: "NASLEĐE & VINOGRAD",
      title: "Od loze do flaše — bez prečica.",
      body1: "Naši vinogradi prostiru se ispod otvorenog neba Jabuke. Od 2015. godine porodica Stanimirović obrađuje lozu rukama, sa istom posvećenošću kao prvog dana.",
      body2: "Deset berbi. Deset porodičnih obreda. Svaka boca čuva godinu koja je prošla i obećava onu koja dolazi.",
      stat1: { value: "2015", label: "Početak porodične priče" },
      stat2: { value: "6", label: "Sorti u proizvodnji" },
      stat3: { value: "100%", label: "Ručno branje" },
    },
    process: {
      eyebrow: "PROCES",
      title: "Tihi rad podruma.",
      body: "Hrastova burad. Čelične kaveze. Etiketirani pampuri. Strpljenje koje meri godine, ne dane.",
      step1: { title: "Hrastova burad", body: "Vino sazreva u finom francuskom hrastu — godinama, ne mesecima." },
      step2: { title: "Čelične kaveze", body: "Boce mirno odležavaju u kontrolisanim uslovima našeg podruma." },
      step3: { title: "Naš pečat", body: "Svaki pampur nosi naš \u201eS\u201c znak — potpis porodice." },
    },
    spotlight: {
      eyebrow: "PROIZVOD U FOKUSU",
      title: "Noćna Tajna",
      tagline: "Tajna ukusa.",
      body: "Tamno, zavodljivo crveno vino. Apstraktna etiketa u dubokoj plavoj i ljubičastoj nagoveštava ono što boca čuva — slojevit, mističan ukus koji se otkriva polako.",
      attrs: ["Tamno voće", "Zrela šljiva", "Hrastova burad", "Dugačak završetak"],
      cta: "Zatražite degustaciju",
    },
    winesSection: {
      eyebrow: "ASORTIMAN",
      title: "Naša vina",
      body: "Šest sorti — svaka sa svojim karakterom.",
    },
    contact: {
      eyebrow: "KONTAKT",
      title: "Posetite naš podrum.",
      body: "Pišite nam, javite se telefonom ili dođite na degustaciju. Vrata su otvorena.",
      name: "Ime i prezime", email: "Email", phone: "Telefon (opciono)", subject: "Naslov (opciono)", message: "Poruka",
      send: "Pošaljite poruku", sending: "Slanje...", success: "Hvala! Vaša poruka je poslata.", error: "Greška. Pokušajte ponovo.",
      address: "Adresa", phoneLabel: "Telefon", emailLabel: "Email", instagram: "Instagram",
    },
    shop: {
      eyebrow: "PRODAVNICA",
      title: "Naručite svoju bocu.",
      body: "Vino se isporučuje direktno iz naše porodične vinarije. Plaćanje po preuzimanju.",
      add: "Dodajte u korpu", added: "U korpi", remove: "Ukloni", qty: "Količina",
      cart: "Korpa", emptyCart: "Vaša korpa je prazna.", total: "Ukupno",
      checkout: "Završite porudžbinu", placing: "Slanje...", placed: "Hvala! Vaša porudžbina je primljena. Kontaktiraćemo Vas uskoro.", error: "Greška pri porudžbini. Pokušajte ponovo.",
      name: "Ime i prezime", phone: "Telefon", email: "Email", address: "Adresa za isporuku", city: "Grad", note: "Napomena (opciono)",
      price: "Cena", rsd: "RSD", perBottle: "po boci 0.75l",
      paymentTitle: "Način plaćanja (po preuzimanju)",
      paymentCash: "Keš", paymentCashDesc: "Plaćanje gotovinom kuriru",
      paymentCard: "Kartica", paymentCardDesc: "Visa ili Master kuriru",
      paymentIps: "IPS", paymentIpsDesc: "Skeniranjem QR koda",
    },
    footer: {
      tag: "Porodična vinarija Stanimirović",
      slogan: "Tajna ukusa.",
      rights: "Sva prava zadržana.",
      address: "Mihajla Pupina 81, Jabuka, Pančevo",
    },
  },
  en: {
    nav: { home: "Home", about: "About Us", wines: "Our Wines", shop: "Shop", contact: "Contact", cart: "Cart", visit: "Visit Us" },
    hero: {
      eyebrow: "FAMILY WINERY",
      title: "Stanimirović",
      subtitle: "Tradition in every drop. Wine born from the silence of the cellar and the warmth of family hands.",
      cta: "Discover Noćna Tajna",
      ctaSecondary: "Our story",
    },
    identity: {
      eyebrow: "OUR IDENTITY",
      title: "One mark. One story.",
      body: "The letter 'S' woven into a wine glass — a quiet summary of everything we do. Devotion, precision and the love of a wine that tells our family history.",
    },
    heritage: {
      eyebrow: "HERITAGE & VINEYARD",
      title: "From the vine to the bottle — no shortcuts.",
      body1: "Our vineyards stretch under the open sky of Jabuka. Since 2015 the Stanimirović family has worked the vines by hand, with the same dedication as on day one.",
      body2: "Ten harvests. Ten family rituals. Every bottle holds the year that has passed and promises the one to come.",
      stat1: { value: "2015", label: "Beginning of our family story" },
      stat2: { value: "6", label: "Varieties produced" },
      stat3: { value: "100%", label: "Hand-picked" },
    },
    process: {
      eyebrow: "PROCESS",
      title: "The quiet work of the cellar.",
      body: "Oak barrels. Steel crates. Stamped corks. Patience that is measured in years, not days.",
      step1: { title: "Oak Barrels", body: "Wine matures in fine French oak — over years, not months." },
      step2: { title: "Steel Crates", body: "Bottles rest in the controlled atmosphere of our cellar." },
      step3: { title: "Our Seal", body: "Every cork carries our 'S' mark — a family signature." },
    },
    spotlight: {
      eyebrow: "PRODUCT SPOTLIGHT",
      title: "Noćna Tajna",
      tagline: "The secret of taste.",
      body: "A dark, seductive red wine. The abstract label in deep blue and purple hints at what the bottle holds — a layered, mysterious flavour that reveals itself slowly.",
      attrs: ["Dark fruit", "Ripe plum", "Oak barrel", "Long finish"],
      cta: "Request a tasting",
    },
    winesSection: {
      eyebrow: "RANGE",
      title: "Our Wines",
      body: "Six varieties — each with its own character.",
    },
    contact: {
      eyebrow: "CONTACT",
      title: "Visit our cellar.",
      body: "Write to us, call us or come for a tasting. The doors are open.",
      name: "Full name", email: "Email", phone: "Phone (optional)", subject: "Subject (optional)", message: "Message",
      send: "Send message", sending: "Sending...", success: "Thank you! Your message has been sent.", error: "Error. Please try again.",
      address: "Address", phoneLabel: "Phone", emailLabel: "Email", instagram: "Instagram",
    },
    shop: {
      eyebrow: "SHOP",
      title: "Order your bottle.",
      body: "Wine ships directly from our family cellar. Cash on delivery.",
      add: "Add to cart", added: "In cart", remove: "Remove", qty: "Quantity",
      cart: "Cart", emptyCart: "Your cart is empty.", total: "Total",
      checkout: "Complete order", placing: "Sending...", placed: "Thank you! Your order has been received. We will contact you shortly.", error: "Order failed. Please try again.",
      name: "Full name", phone: "Phone", email: "Email", address: "Delivery address", city: "City", note: "Note (optional)",
      price: "Price", rsd: "RSD", perBottle: "per 0.75l bottle",
      paymentTitle: "Payment method (on delivery)",
      paymentCash: "Cash", paymentCashDesc: "Pay the courier in cash",
      paymentCard: "Card", paymentCardDesc: "Visa or Master to courier",
      paymentIps: "IPS", paymentIpsDesc: "Scan the QR code",
    },
    footer: {
      tag: "Stanimirović Family Winery",
      slogan: "The secret of taste.",
      rights: "All rights reserved.",
      address: "Mihajla Pupina 81, Jabuka, Pančevo",
    },
  },
};

const I18nContext = createContext({ lang: "sr", t: dict.sr, toggle: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState("sr");
  const toggle = useCallback(() => setLang((l) => (l === "sr" ? "en" : "sr")), []);
  const value = useMemo(() => ({ lang, t: dict[lang], toggle, setLang }), [lang, toggle]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
